import I18n from 'I18n';
import { fromJS } from 'immutable';
import { PERCENTILES, SUM } from 'reporting-data/constants/metricTypes';
import { getDisplayParamsDataColumns, getMetrics } from 'reporting-data/report/configReportGetters';
import getMeasureSizeRanges from '../../../lib/report-utils/getMeasureSizeRanges';
import { DEFAULT_MAX_METRICS_COUNT } from '../constants';
import { flattenPropertyGroups } from '../../../lib/reportPropertyUtils';
import useFetchPropertyOptionsForMetricsEditor from './useFetchPropertyOptionsForMetricsEditor';
import DataColumnsHintFooter from '../../shared-components/dropdown/DataColumnsHintFooter';
import MetricEditor from '../components/MetricEditor';
import { useCallback, useMemo } from 'react';
import { getAggregationOptionsForDropdown } from '../components/utils';
import { setConfigReportMetricByIndex } from 'reporting-data/report/configReportSetters';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { jsx as _jsx } from "react/jsx-runtime";
const isAddMetricButtonDisabled = (report, maxMetricsCount) => {
  const dataColumnsFromReport = getDisplayParamsDataColumns(report) || [];
  const metricsInReport = getMetrics(report);
  const {
    maxMetrics
  } = getMeasureSizeRanges(report);
  const finalMaxMetricsCount = Math.min(maxMetricsCount, maxMetrics);
  let disabledReason = undefined;
  if (metricsInReport.size >= finalMaxMetricsCount) {
    disabledReason = I18n.text('report-customization-components.customization-components.metrics-editor-dropdown.config.tooltip.maxMetrics');
  }
  const isEnabled = dataColumnsFromReport.length > 0 && metricsInReport.size < finalMaxMetricsCount;
  return {
    isDisabled: !isEnabled,
    disabledReason
  };
};
const ConfigReportMetricsEditorDropdown = ({
  report,
  onChange,
  hideAddButton,
  'data-test-id': dataTestId,
  buttonUse,
  maxMetricsCount = DEFAULT_MAX_METRICS_COUNT
}) => {
  const propertyOptions = useFetchPropertyOptionsForMetricsEditor(report);
  const flattedPropertyOptions = flattenPropertyGroups(propertyOptions);
  const dataColumnsFromReport = getDisplayParamsDataColumns(report) || fromJS([]);
  const metricsInReport = useMemo(() => {
    const metrics = getMetrics(report);
    return metrics.map(metric => ({
      property: metric.get('property'),
      aggregation: metric.getIn(['metricTypes', 0])
    })).toJS();
  }, [report]);
  const {
    disabledReason: addMetricDisabledReason
  } = isAddMetricButtonDisabled(report, maxMetricsCount);
  const handleAddMetric = useCallback(() => {
    // Duplicate the first metric or use the first data column
    const firstMetric = metricsInReport[0];
    let property;
    if (firstMetric) {
      property = firstMetric.property;
    } else {
      // Derive the first metric from the first usable property (note - not all datacolumns are usable in metrics editor)
      const firstDataColumn = flattedPropertyOptions[0] || {};
      if (firstDataColumn.options) {
        var _options$;
        property = (_options$ = firstDataColumn.options[0]) === null || _options$ === void 0 ? void 0 : _options$.value;
      } else {
        property = firstDataColumn === null || firstDataColumn === void 0 ? void 0 : firstDataColumn.value;
      }
    }
    const newMetric = fromJS({
      property,
      metricTypes: [SUM]
    });
    const metrics = getMetrics(report);
    const updatedMetrics = metrics.push(newMetric);
    onChange(report.setIn(['config', 'metrics'], updatedMetrics));
  }, [metricsInReport, onChange, flattedPropertyOptions, report]);
  const handleDeleteMetric = useCallback(index => {
    onChange(report.deleteIn(['config', 'metrics', index]));
  }, [onChange, report]);
  const handleMetricChange = useCallback((index, metric) => {
    const updatedMetric = fromJS(Object.assign({
      property: metric.property,
      metricTypes: [metric.aggregation]
    }, metric.aggregation === PERCENTILES && {
      percentiles: [50]
    }));
    onChange(setConfigReportMetricByIndex(report, updatedMetric, index));
  }, [onChange, report]);
  const isDeleteDisabled = useCallback(__index => {
    const {
      minMetrics
    } = getMeasureSizeRanges(report);
    return minMetrics >= metricsInReport.length;
  }, [metricsInReport, report]);
  const getAggregationOptions = useCallback(metricIndex => {
    var _flattedPropertyOptio;
    const reportMetric = metricsInReport[metricIndex];
    const propertyValue = reportMetric.property;
    const aggregationValue = reportMetric.aggregation;
    const propertyMetadata = (_flattedPropertyOptio = flattedPropertyOptions.flatMap(option => option.options || [option]).find(prop => prop.value === propertyValue)) === null || _flattedPropertyOptio === void 0 ? void 0 : _flattedPropertyOptio.metadata;
    return getAggregationOptionsForDropdown(propertyValue, aggregationValue, propertyMetadata);
  }, [metricsInReport, flattedPropertyOptions]);
  if (flattedPropertyOptions.length === 0) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {});
  }
  return /*#__PURE__*/_jsx(MetricEditor, {
    reportMetrics: metricsInReport,
    onAddMetric: handleAddMetric,
    onDeleteMetric: handleDeleteMetric,
    onMetricChange: handleMetricChange,
    propertyOptions: flattedPropertyOptions,
    getAggregationOptions: getAggregationOptions,
    isDeleteDisabled: isDeleteDisabled,
    addMetricDisabledReason: addMetricDisabledReason,
    hideAddButton: hideAddButton || dataColumnsFromReport.length === 0,
    "data-test-id": dataTestId,
    buttonUse: buttonUse,
    dropdownFooter: /*#__PURE__*/_jsx(DataColumnsHintFooter, {})
  });
};
export default ConfigReportMetricsEditorDropdown;